import {
  Stack,
  Typography,
  Grid,
  Box,
  GridProps, //Tooltip
  Tooltip
} from "@mui/material";
import {
  styled,
  useTheme
} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Bookmark, ChevronRight, FileBadge } from "lucide-react";
import {
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useOutletContext
} from "react-router-dom";
import { isAddFavouriteVisible } from "./assertions";
import type { CardCtaProps } from "./CardCta.types";
import {
  Icon,
  Link,
  Modal,
  Spinner
} from "../../components";
import { ButtonDigited, StyledTypography } from "../../components/Button/Button.variants";
import { LEVEL, STATUS_LO } from "../../consts";
import {
  shouldGoToWatchByLabel,
  useButtonStatusMap,
  usePlayResumeVideo
} from "../../hooks";
import {
  useFavouritesMutation,
  useInvalidateLearningObjectQuery,
  useRatingQuery
} from "../../queries";
import { OutletContext } from "../../routes";
import { useNavigate } from "../../services";
import { useAuthStore, useLanguageStore } from "../../stores";
import { getError } from "../../utils/error";
import { getLearningObjectType } from "../../utils/general";
import { Progress } from "../Progress/Progress.component";

const CardCtaGrid = styled(Grid)<
  GridProps & { $vertical?: boolean }
>(({ $vertical = false, theme }) => ({
  alignItems: $vertical ? undefined : "center",
  flexDirection: $vertical ? "column" : undefined,
  position: "relative",
  transition: "gap .3s linear",
  zIndex: theme.zIndex.appBar
}));

export const StyledBoxContact = styled(Box)<{ $visible: boolean }>(({ theme, $visible }) => ({
  alignItems: "center",
  background: theme.customColors.backgroundSecondary,
  borderRadius: "2px",
  boxShadow: "8px 8px 15px rgba(0, 0, 0, 0.15)",
  color: theme.customColors.textInput,
  display: $visible ? "flex" : "none",
  gap: theme.spacing(3),
  justifyContent: "space-between",
  maxHeight: $visible ? "auto" : 0,
  maxWidth: "450px",
  opacity: $visible ? 1 : 0,
  padding: $visible ? theme.spacing(1, 2) : theme.spacing(0, 2),
  position: "absolute",
  top: 0,
  transition: "all .3s linear",
  width: "fit-content",
  zIndex: theme.zIndex.mobileStepper,

  ".icon": {
    cursor: "pointer",
    minWidth: "24px"
  },

  "> *": {
    maxHeight: $visible ? "auto" : 0
  }
}));

export const StyledCreditsTooltipIcon = styled(Box)(({ theme }) => ({
  marginLeft: 10,
  zIndex: `${theme.zIndex.mobileStepper + 1} !important`
}));

export const StyledSnackBarFavourite = styled(Box)<{ $visible: boolean, $cardType?: string }>(({
  theme,
  $visible,
  $cardType
}) => ({
  alignItems: "center",
  background: theme.customColors.backgroundPrimary,
  borderRadius: "2px",
  boxShadow: "8px 8px 15px rgba(0, 0, 0, 0.15)",
  color: theme.customColors.textInput,
  display: "flex",
  gap: theme.spacing(3),
  justifyContent: "space-between",
  maxHeight: $visible ? "auto" : 0,
  maxWidth: "450px",
  opacity: $visible ? 1 : 0,
  padding: $visible ? theme.spacing(1, 2) : theme.spacing(0, 2),
  pointerEvents: $visible ? "auto" : "none",
  position: "absolute",
  right: $cardType === "small" ? 0 : undefined,
  left: $cardType === "small" ? undefined : "10rem",
  top: $cardType === "small" ? "-3.15rem" : "0rem",
  transition: "all .3s linear",
  width: "fit-content",
  zIndex: theme.zIndex.mobileStepper,
  flexWrap: "nowrap",
  whiteSpace: "nowrap",
  ".icon": {
    cursor: "pointer",
    minWidth: "24px"
  },

  ">p, >a": {
    fontSize: "0.75rem"
  },

  "> *": {
    maxHeight: $visible ? "auto" : 0,
    opacity: $visible ? "auto" : 0
  }
}));


export function CardCta({
  badge,
  editionsLength = 0,
  courseId = null,
  disabled = false,
  ecmRegistration,
  enrollType,
  id,
  isBookmarkPage = false,
  isCard = false,
  isSurvey,
  isTest,
  isEditorial,
  madeAttempts,
  ecm_specialization,
  isExpired,
  isStandAlone,
  labelMoreInfo = false,
  learningObjectType,
  learningObjectTypology,
  linkVirtualClass,
  percentageOfCompletion,
  parentId, // for favourites
  grandParentId, // for favourites
  rootId = null,
  setOpenRegistrationModal,
  setOpenModalEcm,
  setOpenTojModal,
  status,
  simple = false,
  triggerOpenTabs,
  vertical = false,
  canAccessNow,
  credits,
  showDetailsButton,
  ctaPosition = "start",
  isLinkRelative = false,
  isExplorePage,
  isHeroBanner = false,
  link,
  cardType,
  attributesIds=[],
  isHovered,
  isFavourite,
  externalFileUrl,
  customBorder,
  categories: _categories,
  contents: _contents,
  attributes: _attributes,
  certificates,
  ...props
}: CardCtaProps) {
  const { sectionUrl } = useOutletContext<OutletContext>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = window.location.href.includes("per-te") ? "per-te" : "esplora";

  const {
    resumeLink,
    sendRequest,
    bannerVisible,
    enrollmentMutationHadSuccess,
    enrollmentIsLoading,
    enrollmentMutationError,
    resumeLinkOpenEsternal,
    enrollmentMutationIsError,
    setBannerVisible,
    actionButton,
    resumeContent
  } = usePlayResumeVideo({
    ecm_specialization,
    ecmRegistration,
    enrollType,
    isCard,
    learningObjectType,
    learningObjectTypology,
    id,
    labelMoreInfo,
    sectionUrl: currentPath,
    status,
    setOpenTojModal,
    setOpenRegistrationModal,
    triggerOpenTabs,
    linkVirtualClass,
    navigate,
    parentId,
    rootId,
    grandParentId,
    courseId,
    isExpired,
    attributesIds
  });

  const buttonText = useButtonStatusMap({
    ecm_specialization: ecm_specialization,
    ecmRegistration,
    enrollType,
    isCard,
    isSurvey: isSurvey,
    isTest: isTest,
    learningObjectType,
    learningObjectTypology,
    madeAttempts: madeAttempts,
    percentageOfCompletion
  });

  const theme = useTheme();
  // const isLoading = useAuthStore(state => state.isLoading);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const language = useLanguageStore(state => state.language)?.id;
  const userData = useAuthStore(state => state.userData);
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const [showModal, setShowModal] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [ariaMessage, setAriaMessage] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Determina se ci troviamo nella pagina bookmarks
  const isBookmarksPage = location.pathname.includes("/bookmarks");

  // Prepara le query da invalidare solo se siamo nella pagina bookmarks
  const getInvalidateContentQueries = () => {
    if (!isBookmarksPage || !userData) return [];

    // Ritorna un array con un semplice marker per indicare che dobbiamo invalidare le query dei preferiti
    return [{
      apiPath: "", // Non serve un path specifico, useremo pattern matching
      isSeedingSession,
      language,
      additionalKeys: []
    }];
  };

  const hasEnabledBadge = !!badge?.isEnable;
  const hasEnabledCertificate = !!certificates?.some(c => c?.isEnable);

  const { invalidateLearningObject } = useInvalidateLearningObjectQuery({
    courseId: courseId,
    id: id,
    learningObjectTypology: learningObjectTypology,
    rootId: rootId
  });

  const { data: ratingStatistic, isLoading: ratingIsLoading } = useRatingQuery({
    enabled: isHovered ?? true,
    learningObjectId: id,
    learningObjectTypology: getLearningObjectType(learningObjectTypology)
  });

  // TO ADD IN PARAMS IN FAVOURITES RETRIEVED FROM LO
  const favParentId = !isStandAlone ? (!courseId && rootId) ? rootId : courseId ?? null : null;

  const favGrandParentId = (!isStandAlone
    && favParentId
    && favParentId !== rootId
  ) ? rootId : null;

  const {
    mutate: addFavouriteMutation,
    isSuccess: isFavouriteAdded,
    isLoading: isAddingFavouriteLoading
  } = useFavouritesMutation({
    grandParentId: favGrandParentId,
    learningObjectId: id,
    learningObjectTypology,
    method: "POST",
    parentId: favParentId,
    invalidateContentQueries: getInvalidateContentQueries()
  });

  const {
    mutate: removeFavouriteMutation,
    isSuccess: isFavouriteRemoved,
    isLoading: isRemovingFavouriteLoading
  } = useFavouritesMutation({
    invalidateContentQueries: getInvalidateContentQueries(),
    invalidateDelay: isBookmarksPage ? 2000 : 0, // 2 secondi di delay solo nella pagina bookmarks
    learningObjectId: id,
    learningObjectTypology,
    method: "DELETE"
  });

  const loStatistic = {
    userFavourite: ratingStatistic
      ? Boolean(ratingStatistic?.userRating.isFavouriteForLearner && ratingStatistic?.objectStatistics?.learningObjectId)
      : Boolean(isFavourite)
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Handle added favourite
    if (isFavouriteAdded && loStatistic.userFavourite) {
      setSnackbarVisible(true);
      setAriaMessage(t("has_been_added"));
      timer = setTimeout(() => {
        setSnackbarVisible(false);
      }, 5000);
    }

    // Handle removed favourite
    else if (isFavouriteRemoved && !loStatistic.userFavourite) {
      setSnackbarVisible(true);
      setAriaMessage(t("has_been_removed"));
      timer = setTimeout(() => {
        setSnackbarVisible(false);
      }, 5000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    isFavouriteAdded,
    isFavouriteRemoved,
    loStatistic.userFavourite
  ]);


  useEffect(() => {
    if (enrollmentMutationHadSuccess && !enrollmentIsLoading) {
      if (enrollType === "AUTO_ENROLL") {
        if (resumeLinkOpenEsternal) {
          window.open(resumeLinkOpenEsternal, "_blank", "noreferrer");
        } else {
          resumeContent();
        }
      } else {
        invalidateLearningObject();
      }

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enrollType,
    enrollmentIsLoading,
    enrollmentMutationHadSuccess,
    navigate,
    resumeLink,
    courseId,
    rootId,
    resumeLinkOpenEsternal
  ]);

  const viewDetails = () => {
    navigate.navigateToDetail({
      id,
      isForYou: currentPath === "per-te",
      learningObjectTypology,
      courseId: courseId ?? parentId,
      rootId: rootId ?? grandParentId
    });
  };

  function handleRedirectUrl() {
    if (isLinkRelative && externalFileUrl) {
      let cleanedLink = externalFileUrl;

      if (!cleanedLink.startsWith("/")) cleanedLink = "/" + cleanedLink;
      const regex = /\/$/; //check if url has / at the end
      if(regex.test(externalFileUrl)){
        return `${cleanedLink}${id}`; //link url and id without / in between
      }
      return `${cleanedLink}/${id}`; //link url and id with / in between
    }
    return `${externalFileUrl}`;
  }

  useEffect(() => {
    enrollmentMutationIsError && setShowModal(true);
  }, [enrollmentMutationIsError]);

  const primaryCtaClick = () => {
    if (disabled) return;

    if (learningObjectTypology === "DA_URL_RES" && isLinkRelative && link) {
      let cleanedLink = link;

      if(!cleanedLink.startsWith("/")){
        cleanedLink = "/" + cleanedLink;
      }

      if(cleanedLink.endsWith("/")){
        cleanedLink = cleanedLink.slice(0, -1);
      }
      navigate(`${cleanedLink}/${id}`);
    } else {
      if (actionButton.function){
        return actionButton.function();
      }else if(!isLinkRelative){
        window.open(resumeLink, "_blank", "noopener,noreferrer");
      }else{
        navigate(resumeLink);
      }
    }
  };

  const showProgressBar = !!(!simple && !isCard && percentageOfCompletion && (status === STATUS_LO.P || status === STATUS_LO.C) && (learningObjectTypology === LEVEL.PATH || learningObjectTypology === LEVEL.COURSE));

  const showPrimaryButton =()=> {
    if(sectionUrl === "per-te") return false; // no primary button in per-te section

    if(getLinkLabel() === t("editions_show") &&  editionsLength === 0) return false; // primary button for editions must be hide if there are no editions

    return true;
  };

  function buttonTypeHandler() {
    if(getLinkLabel() === t("details")){ //TODO condizione da rifinire se necessario
      return "secondary";
    }
    return "primary";
  }

  const primaryButtonHandler = () => {
    if(!isEditorial){
      return <ButtonDigited
        typology={ buttonTypeHandler() }
        buttonSize="md"
        aria-label={ getLinkLabel() }
        onClick={ (e) => {
          e.stopPropagation();
          primaryCtaClick();
        } }
        disabled={ disabled || !canAccessNow }
        text={
          enrollmentIsLoading && (!enrollmentMutationHadSuccess || enrollmentMutationError)
            ? <Spinner />
            : getLinkLabel()
        }
        sxTooltip={ !canAccessNow ? {
          border: `1px solid ${theme.customColors.borderWarning}`,
          background: theme.customColors.backgroundWarning,
          borderRadius: "0.2rem",
          ".MuiTooltip-tooltip":{
            padding:"0.4rem !important",
            margin:"0 !important",
            color: theme.customColors.textTag,
            background: theme.customColors.backgroundWarning
          }
        } : null }
        boxTooltipProps={ {
          alignItems: "center"
        } }
        tooltipMessage={ !canAccessNow ? t("learning_object_access_badge_message") : undefined }
      ></ButtonDigited>;
    }
    if(isEditorial){
      return <Link
        href={ handleRedirectUrl() }
        rel={ isLinkRelative ? undefined : "noopener noreferrer" }
        target={ isLinkRelative ? "_self" : "_blank" }
        aria-label={ t("discover_more") }
      >
        <ButtonDigited
          typology="primary"
          buttonSize="md"
          disabled={ disabled || !canAccessNow }
        >
          <StyledTypography sx={ { color: theme.customColors.textPrimaryCta, display:"flex", alignItems:"center", gap:"0.35rem" } }>{ t("discover_more") + " " }<ChevronRight size="0.875rem" /></StyledTypography>
        </ButtonDigited>
      </Link>;
    }
    return <></>;
  };


  return (
    <CardCtaGrid
      item
      container
      gap={ bannerVisible ? 1 : 0 }
      $vertical={ vertical }
      overflow={ snackbarVisible || bannerVisible ? "visible !important" : "hidden !important" }
      { ...props }
    >
      <StyledSnackBarFavourite
        $visible={ snackbarVisible }
        $cardType={ cardType }
      >
        <Typography
          aria-hidden="true"
          color={ theme.customColors.textPrimary }
          fontSize={ theme.spacing(1.625) }
          fontWeight={ 500 }
        >
          { loStatistic.userFavourite ? t("item_added_favourites") : t("item_removed_favourites") }
          <Link
            aria-hidden="true"
            tabIndex={ -1 }
            onClick={ (e) => {
              e.stopPropagation();
            } }
            href="/bookmarks" fontWeight={ 700 } />
        </Typography>
      </StyledSnackBarFavourite>
      <StyledBoxContact
        $visible={ bannerVisible }
        width="100%"
        sx={ {
          left: 0,
          right: "unset",
          top: "-40px"
        } }
      >
        <Typography
          color={ theme.customColors.textPrimaryAlternative }
          fontSize={ theme.spacing(1.625) }
          fontWeight={ 500 }
        >
          { bannerVisible ? t("contact_text") : "" }
        </Typography>
        <Icon
          aria-hidden="true"
          icon="close"
          size={ 24 }
          color={ theme.palette.primary?.main }
          onClick={ (e) => {
            e.stopPropagation();
            setBannerVisible(false);
          } }
        />
      </StyledBoxContact>
      <Stack
        direction={ "row" }
        gap={ 1 }
        width="100%"
        { ...props }
        sx={ {
          ">button, >a": {
            pointerEvents: setOpenModalEcm && disabled ? "none" : "auto",
            zIndex: setOpenModalEcm && disabled ? "-1" : undefined
          },
          justifyContent: `${ctaPosition === "split" ? "justify-between" : "start"} !important`
        } }
        onClick={ (e) => {
          e.stopPropagation();
          if (setOpenModalEcm && disabled && ecm_specialization && !ecmRegistration) {
            setOpenModalEcm(true);
          }
        } }
      >
        { /* Questa validazine serve ad evitare la presenza del gap in caso in cui il componente stack è vuoto */ }
        { (!(!showProgressBar && !(showDetailsButton && shouldGoToWatchByLabel(buttonText[status]?.label) && !isEditorial))) || (location.pathname !== "/per-te") ?
          <Stack direction={ "row" } flexWrap={ "wrap" } sx={ {
            justifyContent: "start !important",
            width: showProgressBar ? "100%" : "auto"
          } } gap="1rem">
            { /*:TODO emilinare? ma cos'è questa progress??  */ }
            {
              showProgressBar
                ? (
                  <div style={ {
                    flexGrow: 1
                  } }>
                    <Progress
                      color={ props.color || theme.customColors.textPrimary }
                      flexGrow={ 1 }
                      width={ isMobile ? "100%" : "auto" }
                      maxWidth={ "none" }
                      percentageOfCompletion={ percentageOfCompletion }
                    />
                  </div>
                ) : null
            }
            { /*Nella sezione per-te viene nascosto il primary button, e viene sostituito dal primary button presente nella HomeCard , così da
          utilizzare la logica identica al pre-retyle */ }
            { showPrimaryButton() ? primaryButtonHandler() : null }
            { (showDetailsButton && shouldGoToWatchByLabel(buttonText[status]?.label) && (!isEditorial)) ? (
              <ButtonDigited
                buttonSize="md"
                typology="secondary"
                onClick={ (e) => {
                  e.stopPropagation();
                  viewDetails();
                } }
                aria-label={ t("details") }
                disabled={ disabled }
                text={ t("details") }
              />
            ) : null }
          </Stack>
          : null }
        <Stack
          direction={ "row" } alignItems={ "center" } sx={ {
            flex: (ctaPosition === "split" ? 1 : 0),
            justifyContent: `${(ctaPosition === "split") ? "end" : "start"} !important`,
            width: "auto"
          } } gap={ 1 }>

          { credits ? (
            <StyledCreditsTooltipIcon theme={ theme }>
              <Tooltip enterTouchDelay={ 0 } arrow title={ t("learning_object_credits", { credits }) }
                placement={ "top" }>
                <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9.04879 18.289L13.7353 21.5744C14.5409 22.1419 15.6197 22.1419 16.4253 21.5744L27.6477 13.7145C28.9717 12.7898 28.9857 10.8354 27.6758 9.89667L16.4674 1.79159C15.6617 1.21016 14.5689 1.19615 13.7493 1.77058L2.35176 9.75656C1.02077 10.6883 1.02077 12.6637 2.35176 13.5954L6.25369 16.3275"
                    stroke="#F43E63" strokeMiterlimit="10" strokeLinecap="round" />
                  <path d="M14.4429 11.0953L8.6979 15.2038C7.94834 15.7292 7.5 16.5909 7.5 17.5016V24.5016"
                    stroke="#F43E63"
                    strokeMiterlimit="10" strokeLinecap="round" />
                  <path d="M6.24725 21.1969L5.5117 20.6855C4.65006 20.0831 4.13867 19.1023 4.13867 18.0515V14.8852"
                    stroke="#F43E63" strokeMiterlimit="10" strokeLinecap="round" />
                  <path
                    d="M25.8611 16.7062V18.0793C25.8611 19.1371 25.3427 20.1248 24.4741 20.7273L16.8103 26.0302C15.7035 26.7938 14.2394 26.7938 13.1326 26.0162L9.0625 23.1651"
                    stroke="#F43E63" strokeMiterlimit="10" strokeLinecap="round" />
                  <path
                    d="M8.83884 25.1051C8.97194 25.3223 9.05601 25.5744 9.05601 25.8546V27.2557C9.05601 28.0263 8.42553 28.6567 7.65496 28.6567C6.88438 28.6567 6.25391 28.0263 6.25391 27.2557V25.922C6.25391 25.1514 6.72942 24.4993 7.5 24.4993"
                    stroke="#F43E63" strokeMiterlimit="10" strokeLinecap="round" />
                </svg>
              </Tooltip>
            </StyledCreditsTooltipIcon>
          ):null }

          { isAddFavouriteVisible(isHeroBanner, smartConfiguration, rootId, courseId, learningObjectTypology, simple, isBookmarkPage, isEditorial, isExplorePage) ?
            <ButtonDigited
              aria-label={ t(loStatistic.userFavourite ? "remove_from_favourites" : "add_to_favourites") }
              title={ t(loStatistic.userFavourite ? "remove_from_favourites" : "add_to_favourites") }
              disabled={ disabled }
              className="bookmark"
              typology="icononly"
              buttonSize="md"
              onMouseDown={ (e) => e.preventDefault() }
              onClick={ (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (loStatistic.userFavourite) {

                  removeFavouriteMutation();
                } else {
                  addFavouriteMutation();
                }
              } }

            >
              <>
                {
                  (isAddingFavouriteLoading || isRemovingFavouriteLoading || (isHovered && ratingIsLoading)) ?
                    <Spinner />
                    : (
                      <Bookmark
                        fill={ loStatistic.userFavourite ? theme.customColors.textPrimary : "transparent" }
                        stroke={ theme.customColors.textPrimary }
                        aria-hidden={ !loStatistic.userFavourite ? "true" : undefined }
                        aria-label={ snackbarVisible ? "" : t("remove_from_favourites") }
                      />
                    )
                }
                <Box
                  aria-live="polite"
                  sx={ {
                    position: "absolute",
                    width: 1,
                    height: 1,
                    overflow: "hidden",
                    clip: "rect(0 0 0 0)",
                    whiteSpace: "nowrap"
                  } }
                >
                  { ariaMessage }
                </Box>
              </>
            </ButtonDigited>
            : null
          }
          { (sectionUrl === "esplora") &&  (badge || (certificates?.length ?? 0) > 0) ?
            <ButtonDigited
              buttonSize="md"
              typology="icononly"
              disabled={ !hasEnabledBadge && !hasEnabledCertificate }
              onClick={ (e)=> {
                if(hasEnabledBadge || hasEnabledCertificate){
                  e.preventDefault();
                  navigate("/profile");
                } else {
                  e.preventDefault();
                }
              } }
              sx={ {
                cursor:(hasEnabledBadge || hasEnabledCertificate) ? "pointer" :"not-allowed"
              } }
            >
              <FileBadge size={ 16 } color={ theme.customColors.textPrimary } />
            </ButtonDigited>
            : null
          }

        </Stack>
      </Stack>
      <Modal
        open={ showModal && enrollmentMutationIsError }
        onClose={ () => {
          setShowModal(false);
        } }
        description={ getError(enrollmentMutationError)?.[0]?.message ?? "" }
        title={ getError(enrollmentMutationError)?.[0]?.name ?? "" }
      />
    </CardCtaGrid>
  );

  function getLinkLabel() {
    if (isEditorial) {
      return t("more_info");
    }
    if (sendRequest) {
      return t("validation_text");
    }
    if (labelMoreInfo) {
      return t("more_info");
    }
    return t(buttonText[status]?.label);
  }
}
